import { cn } from '../../lib/utils';

/**
 * StatsBar - Dashboard stats overview
 *
 * Displays key metrics in a responsive grid.
 * Dark design with glass morphism effect.
 */
export default function StatsBar({ stats, isSeller, className }) {
  const buyerStats = [
    {
      label: 'Active',
      value: stats.activeEntries,
      icon: TicketIcon,
      color: 'primary',
    },
    {
      label: 'Wins',
      value: stats.wins,
      icon: TrophyIcon,
      color: 'accent',
    },
    {
      label: 'Entered',
      value: stats.totalEntered,
      icon: GridIcon,
      color: 'default',
    },
    {
      label: 'Spent',
      value: formatCurrency(stats.totalSpent),
      icon: CurrencyIcon,
      color: 'default',
      isMonetary: true,
    },
  ];

  const sellerStats = [
    {
      label: 'Active',
      value: stats.activeRaffles,
      icon: FireIcon,
      color: 'accent',
    },
    {
      label: 'Created',
      value: stats.totalCreated,
      icon: PlusIcon,
      color: 'default',
    },
    {
      label: 'Earnings',
      value: formatCurrency(stats.totalEarnings),
      icon: BankIcon,
      color: 'success',
      isMonetary: true,
    },
    {
      label: 'Pending',
      value: stats.pendingHandovers,
      icon: HandoverIcon,
      color: stats.pendingHandovers > 0 ? 'warning' : 'default',
    },
  ];

  const displayStats = isSeller ? sellerStats : buyerStats;

  return (
    <div className={cn('grid grid-cols-2 lg:grid-cols-4 gap-4', className)}>
      {displayStats.map((stat) => (
        <StatCard key={stat.label} {...stat} />
      ))}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, isMonetary }) {
  const colorStyles = {
    default: 'bg-midnight-lighter/50 border-cream/10 text-cream',
    primary: 'bg-primary/10 border-primary/30 text-primary',
    accent: 'bg-accent/10 border-accent/30 text-accent',
    success: 'bg-success/10 border-success/30 text-success',
    warning: 'bg-warning/10 border-warning/30 text-warning',
  };

  const iconColorStyles = {
    default: 'bg-midnight-lighter text-cream/60',
    primary: 'bg-primary/20 text-primary',
    accent: 'bg-accent/20 text-accent',
    success: 'bg-success/20 text-success',
    warning: 'bg-warning/20 text-warning',
  };

  return (
    <div
      className={cn(
        'relative rounded-xl p-4 border backdrop-blur-sm transition-all',
        'hover:border-primary/30',
        colorStyles[color]
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-cream/60 uppercase tracking-wider leading-tight">
            {label}
          </p>
          <p className={cn(
            'text-2xl font-bold mt-1',
            isMonetary ? 'font-mono' : 'font-display'
          )}>
            {value}
          </p>
        </div>
        <div className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
          iconColorStyles[color]
        )}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

// Helper function
function formatCurrency(cents) {
  if (!cents || cents === 0) return '€0';
  return `€${(cents / 100).toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// Icons
function TicketIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  );
}

function TrophyIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3h14M5 3a2 2 0 00-2 2v1a3 3 0 003 3h1m11-6a2 2 0 012 2v1a3 3 0 01-3 3h-1m-5 0v6m-3 0h6m-3-6a3 3 0 01-3-3V5a2 2 0 012-2h2a2 2 0 012 2v4a3 3 0 01-3 3z" />
    </svg>
  );
}

function GridIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function CurrencyIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function FireIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function BankIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function HandoverIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  );
}
